import { useMemo } from 'react';

import {
    isGermanUser,
    checkForScopes,
    allowPvInstallForGermanUsers,
} from '@tools/utils';

import { Scope } from '@tools/enums';
import { TelemetryTypes } from '@store/enums';
import { EnergyFlowScenarioState } from '@store/types';
import {
    useSales,
    useHasGcp,
    useHasSales,
    useHasBattery,
    useTenantData,
    useHasInverter,
    useAppSelector,
    useHasWallbox,
    useHasSmartMeter,
    useHasSmartDevices,
    useHasUkSmartMeter,
    useTelemetries,
    useHasHeatPump,
    useHasElectricCar,
    useIsGridXCustomer,
} from '@store/selectors';

export const useShouldAllowSelfOnboarding = () => {
    const sales = useSales();
    const hasSales = useHasSales();

    return useMemo(
        () =>
            isGermanUser()
                ? sales?.length
                    ? allowPvInstallForGermanUsers(sales[0].steps) // Assuming we have only one active sale
                    : false
                : !hasSales,
        [sales, hasSales],
    );
};

export const useEnergyFlowScenarios = (
    isLive: boolean,
): EnergyFlowScenarioState => {
    const hasGCP = useHasGcp();
    const hasBattery = useHasBattery();
    const hasWallbox = useHasWallbox();
    const hasElectricCar = useHasElectricCar();
    const hasLiveData = useTelemetries(TelemetryTypes.PV_BATTERY).hasLiveData;
    const hasGCPLiveData = useTelemetries(TelemetryTypes.GCP).hasLiveData;
    const hasInverter = useHasInverter();
    const hasSmartMeter = useHasSmartMeter();
    const hasSmartDevices = useHasSmartDevices();
    const hasUkSmartMeter = useHasUkSmartMeter();
    const pvSelfOnboarding = useTenantData('pvSelfOnboarding');
    const hasReadingStartDate = useAppSelector(
        (state) => state.energy.hasReadingStartDate,
    );
    const shouldAllowSelfOnboarding = useShouldAllowSelfOnboarding();
    const hasSmartDevicesWithoutEmobility = useHasSmartDevices(false);
    const hasHeatPump = useHasHeatPump();
    const isGridXCustomer = useIsGridXCustomer();

    const isPvWithSmartMeter = useMemo(
        () => hasSmartMeter && hasInverter,
        [hasInverter, hasSmartMeter],
    );

    const smartMeterOnly = useMemo(
        () => !isPvWithSmartMeter && !hasInverter && hasGCP,
        [hasGCP, hasInverter, isPvWithSmartMeter],
    );

    const isPvWithSmarthome = useMemo(
        () => hasInverter && !hasGCP && (hasSmartDevices || hasHeatPump),
        [hasGCP, hasInverter, hasSmartDevices, hasHeatPump],
    );

    const isSmartMeterOnly = useMemo(
        () => smartMeterOnly && !hasSmartDevices && !hasHeatPump,
        [hasSmartDevices, smartMeterOnly, hasHeatPump],
    );

    const isSmartMeterAndSmartHome = useMemo(
        () => smartMeterOnly && (hasSmartDevices || hasHeatPump),
        [hasSmartDevices, smartMeterOnly, hasHeatPump],
    );

    const isUkSmartMeterNoData = useMemo(
        () => hasUkSmartMeter && !hasReadingStartDate,
        [hasReadingStartDate, hasUkSmartMeter],
    );

    const isUkSmartMeter = useMemo(
        () =>
            hasUkSmartMeter &&
            hasReadingStartDate &&
            !hasInverter &&
            !hasBattery &&
            !hasSmartDevices &&
            !hasHeatPump,
        [
            hasUkSmartMeter,
            hasSmartDevices,
            hasReadingStartDate,
            hasBattery,
            hasInverter,
            hasHeatPump,
        ],
    );

    const hasNoDevices = useMemo(
        () =>
            !hasInverter &&
            !hasGCP &&
            !hasSmartDevices &&
            !hasWallbox &&
            !hasElectricCar &&
            !hasUkSmartMeter &&
            !hasElectricCar &&
            !hasHeatPump,
        [
            hasGCP,
            hasInverter,
            hasSmartDevices,
            hasUkSmartMeter,
            hasWallbox,
            hasHeatPump,
            hasElectricCar,
        ],
    );

    // Disabled in https://jira.eon.com/browse/HS-11800
    // const shouldShowSmartMeterUI = useMemo(
    //     () =>
    //         isGBUser() &&
    //         source === UserModel.SourceEnum.OpenRegistration &&
    //         !hasUkSmartMeter,
    //     [hasUkSmartMeter, source],
    // );

    const shouldShowSmartMeterUI = false;

    const isPVonly = useMemo(
        () =>
            !hasBattery &&
            !hasGCP &&
            !hasUkSmartMeter &&
            !hasSmartDevices &&
            !hasHeatPump,
        [hasGCP, hasSmartDevices, hasUkSmartMeter, hasBattery, hasHeatPump],
    );

    const isSmartHomeOnly = useMemo(
        () =>
            !hasInverter &&
            !hasGCP &&
            !hasBattery &&
            !hasUkSmartMeter &&
            hasSmartDevices,
        [hasGCP, hasSmartDevices, hasUkSmartMeter, hasBattery, hasInverter],
    );

    const hasNoLiveData = useMemo(
        () =>
            isLive &&
            ((hasInverter && !hasLiveData) ||
                (isGridXCustomer && hasGCP && !hasGCPLiveData)),
        [
            hasInverter,
            hasLiveData,
            isLive,
            hasGCP,
            isGridXCustomer,
            hasGCPLiveData,
        ],
    );

    const shouldShowSelfOnboardingButton = useMemo(
        () =>
            !hasInverter &&
            !!pvSelfOnboarding &&
            !shouldShowSmartMeterUI &&
            shouldAllowSelfOnboarding &&
            checkForScopes([Scope.ENERGYDEVICES_PVB_WRITE]),
        [
            hasInverter,
            pvSelfOnboarding,
            shouldShowSmartMeterUI,
            shouldAllowSelfOnboarding,
        ],
    );

    return {
        isPVonly,
        hasWallbox,
        hasElectricCar,
        hasInverter,
        hasNoDevices,
        hasSmartMeter,
        hasNoLiveData,
        smartMeterOnly,
        isUkSmartMeter,
        hasUkSmartMeter,
        hasSmartDevices,
        isSmartHomeOnly,
        isSmartMeterOnly,
        isPvWithSmarthome,
        isPvWithSmartMeter,
        isUkSmartMeterNoData,
        shouldShowSmartMeterUI,
        isSmartMeterAndSmartHome,
        shouldShowSelfOnboardingButton,
        hasSmartDevicesWithoutEmobility,
        hasHeatPump,
    };
};
